import {Box, Slider} from '@mui/material';
import React, {useEffect, useMemo} from 'react';
import {range} from 'lodash';
import {useAccountContext, useAccountFull} from '../../../contexts/AccountContext';
import {ErrorPage} from '../../../pages/ErrorPage';
import {AutoField} from '../../../components/AutoForm/AutoFieldNew';
import Loading from '../../../components/Loading/Loading';
import {OBJECTIVE_INCOME, RATE_MAX, RATE_MIN} from './constants';
import {
  capitalRateField,
  incomeRateField,
  targetAgeField,
  targetCapitalField,
  targetEndDateField,
  targetIncomeField,
  targetYearsField,
} from './goalFields';
import {CAPITAL_MARKS, INCOME_MARKS, LogSlider} from './LogSlider';
import {GoalFormValues, GoalPartProps} from './utils';
import {
  getClientAge,
  getTargetFields,
  onUpdateTargetAgeFactory,
  onUpdateTargetDateFactory,
  onUpdateTargetYearsFactory,
  setGoalAmountOutput,
  targetAgeBusinessRules,
} from './GoalAmountsHelper';
import {UPDATES} from '../../../shared/UPDATES';
import {updateGoalFormName} from './GoalReasonsHelper';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';

const containerCSS = {
  display: 'flex',
  flexDirection: 'row',
  marginBottom: '3%',
};
const sliderCSS = {
  width: '65%',
  marginRight: '5%',
};
const textCSS = {
  width: '30%',
  margin: 'auto',
};

export default function GoalAmounts(props: GoalPartProps) {
  const {setGoalFormName} = props;
  const {values, setFieldValue} = useAutoForm<GoalFormValues>();
  const {accountLoading, accountError} = useAccountContext();
  const {accountFull: accountDetailed} = useAccountFull();
  const isIncome = values.planningObjective === OBJECTIVE_INCOME;
  const {target, rate, output} = getTargetFields(isIncome);
  const hasTargetAge = targetAgeBusinessRules(values);

  const onUpdateTargetYears = onUpdateTargetYearsFactory({
    values,
    account: accountDetailed!,
    setFieldValue,
  });

  const onUpdateTargetAge = onUpdateTargetAgeFactory({
    values,
    account: accountDetailed!,
    setFieldValue,
  });

  const onUpdateTargetDate = onUpdateTargetDateFactory({
    values,
    account: accountDetailed!,
    setFieldValue,
  });

  const rateField = useMemo(() => {
    if (isIncome) {
      return incomeRateField;
    }
    return capitalRateField;
  }, [isIncome]);

  const incomeField = useMemo(() => {
    if (isIncome) {
      return targetIncomeField;
    }
    return targetCapitalField;
  }, [isIncome]);

  useEffect(() => {
    setGoalAmountOutput({
      values,
      target,
      setFieldValue,
      rate,
      output,
    });
  }, [values[target], values[rate]]);

  useEffect(() => {
    // initialise values if none already exist
    if (values.id || values.targetAge) {
      return;
    }
    const clientAge = getClientAge(values, accountDetailed!);
    setFieldValue(UPDATES._1109, 'targetAge', clientAge, true);
    onUpdateTargetAge(clientAge);
  }, [values.id]);

  useEffect(() => {
    updateGoalFormName({setFieldValue, setGoalFormName, values});
  }, [JSON.stringify(values)]);

  if (accountLoading) {
    return <Loading />;
  }

  if (accountError) {
    return (
      <ErrorPage
        code="Server Error"
        text="We were unable to retrieve the account information."
      />
    );
  }

  return (
    <Box mt={3} ml={5} height="100%">
      <Box mt={5} ml={1} width="90%" height="100%">
        <Box sx={containerCSS}>
          <Box sx={sliderCSS}>
            <LogSlider
              marks={isIncome ? INCOME_MARKS : CAPITAL_MARKS}
              field={target}
              values={values}
              onChange={(v: number) => {
                setFieldValue(UPDATES._3600, target, v);
              }}
            />
          </Box>
          <Box sx={textCSS}>
            <AutoField field={incomeField} />
          </Box>
        </Box>
        <Box sx={containerCSS}>
          <Box sx={sliderCSS}>
            <Slider
              key={`${values[rate]}`}
              value={values[rate]}
              onChange={(_, v) => {
                setFieldValue(UPDATES._3601, rate, v);
                // this is what is sent to curo
                setFieldValue(UPDATES._3602, 'drawdownRate', v);
              }}
              min={RATE_MIN}
              max={RATE_MAX}
              marks={range(RATE_MIN, RATE_MAX + 1).map((v) => {
                return {value: v, label: `${v}%`};
              })}
              step={0.5}
            />
          </Box>
          <Box sx={textCSS}>
            <AutoField field={rateField} key={`${values[rate]}`} />
          </Box>
        </Box>
        <Box sx={containerCSS}>
          <Box sx={sliderCSS}>
            <LogSlider
              field={output}
              disabled
              values={values}
              marks={isIncome ? CAPITAL_MARKS : INCOME_MARKS}
            />
          </Box>
          <Box sx={textCSS}>
            <AutoField
              field={
                isIncome
                  ? {...targetCapitalField, disabled: true}
                  : {...targetIncomeField, disabled: true}
              }
              key={`${values[output]}`}
            />
          </Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="row" mt={10}>
        {hasTargetAge && (
          <Box width="30%" marginRight="15%" display="flex">
            <AutoField
              onBlur={(e) => onUpdateTargetAge(e.target.value)}
              field={targetAgeField}
            />
          </Box>
        )}
        <Box width="20%" marginRight="15%">
          {/* sorry, just forcing an update with this funky key below */}
          <AutoField
            field={targetEndDateField}
            onBlur={(e) => onUpdateTargetDate((e.target as any).value)}
          />
        </Box>
        <Box width="20%">
          <AutoField
            onBlur={(e) => onUpdateTargetYears(e.target.value)}
            field={targetYearsField}
          />
        </Box>
      </Box>
    </Box>
  );
}
